<template>
  <component :is="el" :label="label" :prop="prop" :required="required" :for="noForm ? null : inputId">
    <!-- XOL-674: opt-in `label` slot, passed to the el-form-item (falls back to the `label` prop). Rendered only
         when the caller fills it, so every other use keeps exactly the label element it had. -->
    <template v-if="$scopedSlots.label && !noForm" #label><slot name="label">{{label}}</slot></template>
    <div v-if="noForm">{{label}}&nbsp;</div>
    <el-switch
      ref="switch"
      v-model="formData"
      :id="inputId"
      :aria-label="accessibleName"
      :disabled="disabled"
      :size="size"
      :placeholder="placeholder"
      :active-color="onColor"
      :inactive-color="offColor"
      :active-text="isDesktop ? onText : null"
      :inactive-text="isDesktop ? offText : null"
      :active-close-icon="onIcon"
      :inactive-close-icon="offIcon"
      @change="updated">
    </el-switch>
  </component>
</template>

<script>

export default {
  name: 'switch-input',


  props: {

    label: {
      type: String,
      default: null
    },

    value: {
      required: true
    },

    disabled: {
      type: Boolean,
      default: false
    },

    required: {
      type: Boolean,
      default: false
    },

    size: {
      type: String,
      default: 'default'
    },

    onText: {
      type: String,
      default: 'Yes'
    },

    offText: {
      type: String,
      default: 'No'
    },

    onIcon: {
      type: String,
      default: 'fa fa-check-circle'
    },

    offIcon: {
      type: String,
      default: 'fa fa-times-circle'
    },

    onColor: {
      type: String,
      default: '#67C23A'
    },

    offColor: {
      type: String,
      default: '#F56C6C'
    },

    placeholder: {
      type: String,
      default: null
    },

    helpText: {
      type: String,
      default: null
    },

    prop: {
      type: String,
      default: null
    },

    noForm: {
      type: Boolean,
      default: false
    },

    // XOL-674: opt-in. When set, the switch's focusable input gets this id and the form item's <label>
    // points at it (for=). With it the input and the role="switch" root both carry the field's name
    // (see `accessibleName`). null (the default) leaves the markup exactly as it was.
    inputId: {
      type: String,
      default: null
    },

    // XOL-674: the accessible name when the visible label is a bare word or a slot ("Upload" is shown,
    // "Upload Alerts" is announced). Applied whenever it is given; with `inputId` set and no ariaLabel
    // the visible `label` is the name.
    ariaLabel: {
      type: String,
      default: null
    }

  },



  computed: {

    // XOL-674: null for every use that sets neither prop, so their markup is unchanged.
    accessibleName () {
      return this.ariaLabel || (this.inputId ? this.label : null)
    }

  },



  methods: {

    // XOL-674: the focused element is the hidden input.el-switch__input, which takes its name from
    // <label for>. When the label holds a HelpTip (role="img" + aria-label) that name becomes
    // "Upload" + the tip's sentence. An aria-label on the input wins over the label text. el-switch
    // exposes no prop for it, so it is set on the element (and a click on the label still toggles).
    applyInputName () {
      var sw = this.$refs.switch
      var input = sw && sw.$refs ? sw.$refs.input : null

      if (!input) {
        return
      }

      if (this.accessibleName) {
        input.setAttribute('aria-label', this.accessibleName)
      } else if (input.hasAttribute('aria-label')) {
        input.removeAttribute('aria-label')
      }
    },

    updated (value) {
      this.$emit('input', value)
      this.$emit('change', value)
    }

  },



  watch: {
    value: function (val) {
      this.formData = val
    },

    accessibleName: function () {
      this.applyInputName()
    }
  },


  mounted () {
    this.applyInputName()
  },


  data () {

    return {
      formData: this.value,
      el: this.noForm ? 'div' : 'el-form-item'
    }

  }

}
</script>
